import React, { useState } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import Icon from 'components/base/Icon';
import { DefinitionLevels } from '@linearb/linjs-common';
import MetricsBenchmarksTooltip from 'components/MetricsBenchmarkTooltip';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import { Tooltip } from '@material-ui/core';
import Text from 'components/base/Text';
import useSettingsService from './hooks/useSettingsService';
import {
  getBenchmarkRange,
  isDisplayBenchmark
} from 'common/services/benchmarks.service';
import useMetricsCalculationConfig from '../views/root/views/home/hooks/useMetricsCalculationConfig';
import useBenchmarkOrgSizeConfig from 'views/root/views/home/hooks/useBenchmarkOrgSizeConfig';

const BenchmarkIconWrapper = styled.div`
  margin: ${({ margin }) => margin};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  cursor: pointer;
`;

const getTooltip = ({ title, isBenchmarkPending }) => {
  const benchmarkPendingComment = isBenchmarkPending
    ? ' (data collection in progress)'
    : '';
  return (
    <Text color='shark'>
      Engineering Metrics Benchmarks{benchmarkPendingComment}:{' '}
      <strong>{_.capitalize(title)}</strong>
    </Text>
  );
};

const MetricsBenchmarkIcon = ({
  metricName,
  value,
  size = 'large',
  margin = null,
  selectedTeamId,
  isBenchmarkPending,
  hideSettingsLink = false,
  granularity = null,
  calculationStrategy,
  popperPlacement = 'bottom-end'
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { getSettingOrDefinitionValue } = useSettingsService();

  const definitionLevel = selectedTeamId
    ? DefinitionLevels.TEAM
    : DefinitionLevels.ORGANIZATION;
  const { metricsCalculationStrategy } = useMetricsCalculationConfig();
  const { benchmarkOrgSize } = useBenchmarkOrgSizeConfig();

  const benchmarkRange = getBenchmarkRange({
    metricName,
    value,
    granularity,
    metricsCalculationStrategy:
      calculationStrategy ?? metricsCalculationStrategy,
    benchmarkOrgSize
  });
  const isBenchmarkValid = benchmarkRange && _.isNumber(value);

  const isDisplay =
    isDisplayBenchmark({ getSettingOrDefinitionValue, selectedTeamId }) &&
    isBenchmarkValid;

  const {
    icon: benchmarkIcon,
    icon_color: benchmarkIconColor,
    title = ''
  } = benchmarkRange || {};

  const benchmarkOnClick = event => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: window.location.pathname,
        click_type: TRACKING.CLICK.VIEW_METRICS_BENCHMARKS,
        metric_name: metricName
      });
    } else {
      setAnchorEl(null);
    }
  };

  return (
    <>
      <Tooltip title={getTooltip({ title, isBenchmarkPending })}>
        <BenchmarkIconWrapper
          fillColor={benchmarkIconColor}
          margin={margin}
          isVisible={isDisplay}
        >
          {isDisplay && (
            <Icon
              onClick={benchmarkOnClick}
              name={benchmarkIcon}
              size={size}
              color={benchmarkIconColor}
              alpha={isBenchmarkPending ? 0.4 : 1}
            />
          )}
        </BenchmarkIconWrapper>
      </Tooltip>
      <MetricsBenchmarksTooltip
        value={value}
        metricName={metricName}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        definitionLevel={definitionLevel}
        hideSettingsLink={hideSettingsLink}
        granularity={granularity}
        calculationStrategy={calculationStrategy}
        popperPlacement={popperPlacement}
      />
    </>
  );
};

export default MetricsBenchmarkIcon;
